/**
 * 设备信息工具
 * 后端登录/注册相关接口需要在请求体里带上 deviceInfo。
 * 此处面向 Web 端，对齐后端 DeviceInfoDTO。
 *
 * - Web 端：优先 navigator.userAgentData.getHighEntropyValues；
 *   不支持时从 navigator.userAgent / screen / vendor 推断；
 *   deviceId 通过 localStorage 持久化。
 * - 小程序 / App 端：可通过 override 参数由外部传入覆盖。
 */
import type { DeviceInfo } from '@xianlaibang/shared/types'

const DEVICE_ID_KEY = 'xianlaibang_device_id'
const PLATFORM = 7

function generateDeviceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getOrCreateDeviceId() {
  if (typeof window === 'undefined') return ''
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY)
    if (!id) {
      id = generateDeviceId()
      localStorage.setItem(DEVICE_ID_KEY, id)
    }
    return id
  } catch (e) {
    return generateDeviceId()
  }
}

/* ----------------------------- UA 解析 ----------------------------- */

function detectOsNameFromUa(ua: string): string {
  if (/windows/i.test(ua)) return 'windows'
  if (/harmony/i.test(ua)) return 'harmonyos'
  if (/android/i.test(ua)) return 'android'
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/macintosh|mac os x/i.test(ua)) return 'macos'
  if (/linux/i.test(ua)) return 'linux'
  return 'web'
}

function detectOsNameFromPlatform(platform: string): string {
  const p = (platform || '').toLowerCase()
  if (!p) return ''
  if (p.includes('win')) return 'windows'
  if (p.includes('mac')) return 'macos'
  if (p.includes('iphone') || p.includes('ipad') || p.includes('ios')) return 'ios'
  if (p.includes('android')) return 'android'
  if (p.includes('harmony')) return 'harmonyos'
  if (p.includes('linux')) return 'linux'
  return p
}

function detectOsVersionFromUa(ua: string): string {
  let m = ua.match(/Windows NT ([0-9.]+)/)
  if (m) {
    const map: Record<string, string> = {
      '10.0': '10',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
      '6.0': 'Vista',
      '5.2': 'XP',
      '5.1': 'XP'
    }
    return map[m[1]] || m[1]
  }
  m = ua.match(/Mac OS X (?:10[._])?(\d+[._]\d+(?:[._]\d+)?)/i) || ua.match(/Macintosh;.*Mac OS X (10[._]\d+[._]\d+)/i)
  if (m) return m[1].replace(/_/g, '.')

  m = ua.match(/OS (\d+[._]\d+(?:[._]\d+)?) like Mac OS X/) || ua.match(/CPU OS (\d+[._]\d+(?:[._]\d+)?)/)
  if (m) return m[1].replace(/_/g, '.')

  m = ua.match(/Android[-/ ](\d+(?:\.\d+)?)/i)
  if (m) return m[1]

  m = ua.match(/HarmonyOS[-/ ]?(\d+(?:\.\d+)?)/i) || ua.match(/OpenHarmony[-/ ]?(\d+(?:\.\d+)?)/i)
  if (m) return m[1]

  return ''
}

function detectBrandFromUa(ua: string): string {
  if (/iphone|ipad|ipod/i.test(ua)) return 'apple'
  if (/\b(huawei|honor)\b/i.test(ua)) {
    return /honor/i.test(ua) ? 'honor' : 'huawei'
  }
  if (/\b(xiaomi|mi-|redmi|poco)\b/i.test(ua)) return 'xiaomi'
  if (/\b(oppo|pecl00|pehm00)\b/i.test(ua)) return 'oppo'
  if (/\b(vivo|v\d{4})\b/i.test(ua)) return 'vivo'
  if (/\b(samsung|sm-[a-z]\d+)\b/i.test(ua)) return 'samsung'
  if (/\b(oneplus)\b/i.test(ua)) return 'oneplus'
  if (/\b(meizu|m\d{2,3})\b/i.test(ua)) return 'meizu'
  if (/\b(nokia)\b/i.test(ua)) return 'nokia'
  if (/\b(google|pixel)\b/i.test(ua)) return 'google'
  if (/\b(lenovo|l780)\b/i.test(ua)) return 'lenovo'
  if (/\b(zte)\b/i.test(ua)) return 'zte'
  return ''
}

function detectModelFromUa(ua: string, brand: string): string {
  let m = ua.match(/iPhone(\d+,\d+)/)
  if (m) return `iPhone ${m[1]}`

  m = ua.match(/\b(HUAWEI|HONOR|Xiaomi|Redmi|OPPO|vivo|Samsung)[ /-]+([A-Za-z0-9 \-]+?)(?=[);\/]|$)/i)
  if (m) return `${m[1]} ${m[2].trim()}`

  m = ua.match(/\bSM-([A-Z]\d{3,4}[A-Z]?)\b/)
  if (m) return `SM-${m[1]}`

  m = ua.match(/\b(Pixel \d+[a-zA-Z ]*)\b/)
  if (m) return m[1].trim()

  m = ua.match(/\b(MacBookPro|MacBookAir|MacBook|MacPro|Mac mini|iMac)\b[\d,]*/i)
  if (m && /Macintosh|Mac OS/i.test(ua)) return m[0]

  return ''
}

function detectDeviceType(ua: string, screenWidth?: number): string {
  if (/ipad|tablet|playbook/i.test(ua)) return 'pad'
  if (/iphone|mobile|android/i.test(ua)) {
    if (screenWidth && screenWidth >= 768) return 'pad'
    return 'phone'
  }
  return 'pc'
}

/* ------------------------- navigator.userAgentData ------------------------- */

interface UAData {
  brands?: { brand: string; version: string }[]
  mobile?: boolean
  platform?: string
  architecture?: string
  bitness?: string
  model?: string
  platformVersion?: string
  uaFullVersion?: string
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>
}

function getUAD(): UAData | null {
  if (typeof navigator === 'undefined') return null
  return (navigator as any).userAgentData || null
}

/* ----------------------------- 浏览器解析 ----------------------------- */

interface BrowserInfo {
  name: string
  version: string
}

function detectBrowserFromUa(ua: string): BrowserInfo {
  if (!ua) return { name: '', version: '' }
  let m = ua.match(/Edg\/(\d+(?:\.\d+)*)/)
  if (m) return { name: 'Edge', version: m[1] }
  m = ua.match(/OPR\/(\d+(?:\.\d+)*)/)
  if (m) return { name: 'Opera', version: m[1] }
  if (/QQBrowser\/(\d+(?:\.\d+)*)/.test(ua)) return { name: 'QQBrowser', version: ua.match(/QQBrowser\/(\d+(?:\.\d+)*)/)![1] }
  if (/UBrowser\/(\d+(?:\.\d+)*)/.test(ua)) return { name: 'UC', version: ua.match(/UBrowser\/(\d+(?:\.\d+)*)/)![1] }
  if (/QihooBrowser\/(\d+(?:\.\d+)*)/.test(ua)) return { name: '360', version: ua.match(/QihooBrowser\/(\d+(?:\.\d+)*)/)![1] }
  if (/Maxthon\/(\d+(?:\.\d+)*)/.test(ua)) return { name: 'Maxthon', version: ua.match(/Maxthon\/(\d+(?:\.\d+)*)/)![1] }
  if (/baidubrowser\/([\w.]+)/i.test(ua)) return { name: 'Baidu', version: ua.match(/baidubrowser\/([\w.]+)/i)![1] }
  m = ua.match(/Chrome\/(\d+(?:\.\d+)*)/)
  if (m) return { name: 'Chrome', version: m[1] }
  m = ua.match(/Version\/(\d+(?:\.\d+)*).*Safari/)
  if (m) return { name: 'Safari', version: m[1] }
  m = ua.match(/Firefox\/(\d+(?:\.\d+)*)/)
  if (m) return { name: 'Firefox', version: m[1] }
  return { name: '', version: '' }
}

/**
 * 收集当前设备的 DeviceInfo
 * @param override 由外部传入的字段覆盖（小程序 / App 可注入 uni.getDeviceInfo 等）
 */
export function collectDeviceInfo(override: Partial<DeviceInfo> = {}): DeviceInfo {
  if (typeof window === 'undefined') {
    return { ...override, platform: PLATFORM } as DeviceInfo
  }

  const ua = navigator.userAgent || ''
  const screenW = typeof screen !== 'undefined' ? Math.max(screen.width, screen.height) : undefined
  const vendor = navigator.vendor || ''

  const uad = getUAD()
  const browser = detectBrowserFromUa(ua)
  const browserNameOnly = browser.name || ''
  const browserStr = browser.name ? `${browser.name} ${browser.version}`.trim() : ''

  const uadPlatform = (uad && uad.platform) || ''
  const uadModel = (uad && uad.model) || ''
  const uadPlatformVersion = (uad && uad.platformVersion) || ''
  const uadFullVersion = (uad && uad.uaFullVersion) || ''

  if (uad && typeof uad.getHighEntropyValues === 'function') {
    try {
      uad.getHighEntropyValues(['model', 'platformVersion', 'uaFullVersion'])
        .then(() => undefined)
        .catch(() => undefined)
    } catch (e) {
      /* ignore */
    }
  }

  const osNameFromPlatform = detectOsNameFromPlatform(uadPlatform)
  const osNameFromUa = detectOsNameFromUa(ua)
  const osName = override.osName || osNameFromPlatform || osNameFromUa || 'web'

  const osVersion =
    override.osVersion ||
    uadPlatformVersion ||
    uadFullVersion ||
    detectOsVersionFromUa(ua)

  const brandFromUa = detectBrandFromUa(ua)
  const deviceBrand =
    override.deviceBrand ||
    brandFromUa ||
    browserNameOnly ||
    ''

  const uaModel =
    uadModel ||
    detectModelFromUa(ua, deviceBrand) ||
    ''

  const deviceModel =
    override.deviceModel ||
    uaModel ||
    browserStr ||
    ''

  const deviceType =
    override.deviceType ||
    (uad && uad.mobile === false
      ? 'pc'
      : uad && uad.mobile === true
        ? 'phone'
        : detectDeviceType(ua, screenW))

  return {
    deviceId: override.deviceId || getOrCreateDeviceId(),
    deviceBrand,
    deviceModel,
    deviceType,
    osName,
    osVersion,
    appVersion: override.appVersion || '',
    appVersionCode: override.appVersionCode || '',
    ...override,
    platform: PLATFORM
  } as DeviceInfo
}

export function useDeviceInfo() {
  return {
    getDeviceInfo: (override: Partial<DeviceInfo> = {}) => collectDeviceInfo(override)
  }
}

export function getDeviceInfo(override: Partial<DeviceInfo> = {}) {
  return collectDeviceInfo(override)
}
